// React Dependencies
import React, { useRef, useState, useMemo } from 'react';

// External Dependencies
import MaterialTable from 'material-table';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { IconButton, Button, Dialog, DialogContent, DialogActions, Typography, Select, MenuItem, makeStyles } from '@material-ui/core';
import { Delete as DeleteIcon, Edit as EditIcon } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

// Internal Dependencies
import ApiService from '../../../services.js/apiService';
import { Colours, Status } from '../../../common/constants';
import { toggleModal, updateModalContent } from '../../../actions/modal';

const useStyles = makeStyles(() => ({
  rowButton: {
    textTransform: 'none',
    marginLeft:    '.5rem',
  },
}));

const ModalContext = { Add: 'Add', Delete: 'Delete' };
const defaultModalState = { open: false, context: ModalContext.Add, id: null };

// eslint-disable-next-line no-shadow
const LoyaltyCards = ({ match, updateModalContent, toggleModal }) => {
  const { url } = match;
  const { id: pageId } = match.params;

  const tableRef = useRef(null);
  const history = useHistory();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState(defaultModalState);

  const fetchTableData = (query) => new Promise(async (resolve) => {
    try {
      setLoading(true);
      query.pageNo = query.page;
      const { page } = query;
      const queryFilters = Object.fromEntries(query.filters.map((filters) => [filters.column.field, filters.value]));
      const result = await ApiService.getLoyaltyCards(pageId, { ...query, ...queryFilters });

      resolve({ data: result.rows, page, totalCount: result.totalCount });
    } catch (err) {
      toast.error(err.message);
      resolve({ data: [], page: 0, totalCount: 0 });
    } finally {
      setLoading(false);
    }
  });

  const handleDelete = (id) => {
    updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this loyalty card will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            setLoading(true);

            await ApiService.deleteLoyaltyCard(pageId, id);

            tableRef.current.onQueryChange();
            toast.success('Successfully deleted loyalty card');
          } catch (err) {
            toast.error(err.message);
          } finally {
            setLoading(false);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });
    toggleModal();
  };

  const addNewLoyaltyCard = async () => {
    try {
      setLoading(true);
      setModalState(defaultModalState);

      const newLoyaltyCard = await ApiService.createLoyaltyCard(pageId, {});
      history.push(`${url}/${newLoyaltyCard.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const defaultColumns = useMemo(() => [
    {
      title:     'ID',
      filtering: true,
      editable:  'never',
      field:     'id',
      cellStyle: { width: '15%' },
    },
    {
      title:     'Name',
      filtering: true,
      editable:  'never',
      field:     'name',
      cellStyle: { width: '45%' },
    },
    {
      title:           'Status',
      field:           'status',
      filtering:       true,
      editable:        'never',
      cellStyle:       { width: '25%' },
      filterComponent: (props) => (
        <Select
          className="w-100"
          // eslint-disable-next-line react/prop-types
          onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
        >
          <MenuItem value="">-</MenuItem>
          <MenuItem value={Status.Draft}>{Status.Draft}</MenuItem>
          <MenuItem value={Status.Live}>{Status.Live}</MenuItem>
          <MenuItem value={Status.Deactivated}>{Status.Deactivated}</MenuItem>
        </Select>
      ),
    },
    {
      title:     'Actions',
      cellStyle: { width: '15%' },
      filtering: false,
      render:    (row) => (
        <div className="container-fluid d-flex align-items-center justify-content-end">
          {[Status.Draft, Status.Deactivated].includes(row.status) && (
            <IconButton
              onClick={() => handleDelete(row.id)}
              className={classes.rowButton}
            >
              <DeleteIcon htmlColor={Colours.Black} />
            </IconButton>
          )}
          <IconButton
            onClick={() => window.open(`${url}/${row.id}`, '_blank')}
            className={classes.rowButton}
          >
            <EditIcon htmlColor={Colours.Black} />
          </IconButton>
        </div>
      ),
    },
  ], []);

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={modalState.open}
        onClose={() => setModalState({ ...modalState, open: false })}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        onTransitionEnd={() => !modalState.open && setModalState(defaultModalState)}
      >
        <DialogContent>
          <div style={{ marginTop: 15 }}>
            <Typography style={{ fontSize: 16, fontWeight: 'bold' }}>Create a new loyalty card?</Typography>
          </div>
        </DialogContent>

        <DialogActions>
          <Button color="primary" onClick={() => setModalState(defaultModalState)}>
            Cancel
          </Button>
          <Button color="primary" onClick={addNewLoyaltyCard}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <div className="container-fluid d-flex align-items-center mb-3">
        <Typography variant="h1" className="font-weight-bold">
          Loyalty Cards
        </Typography>
        <Button className="ml-4 px-3" onClick={() => setModalState({ open: true, context: ModalContext.Add })} variant="contained" color="primary">+ Add</Button>
      </div>

      <div className="container-fluid">
        <MaterialTable
          tableRef={tableRef}
          columns={defaultColumns}
          isLoading={loading}
          components={{ Toolbar: () => null }}
          options={{ filtering: true, search: false, debounceInterval: 500, emptyRowsWhenPaging: false }}
          data={fetchTableData}
        />
      </div>
    </>
  );
};

LoyaltyCards.propTypes = {
  match: PropTypes.shape({
    url:    PropTypes.string,
    params: PropTypes.shape(Object),
  }).isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(null, {  toggleModal, updateModalContent })(LoyaltyCards);
